import { useState } from "react";
import { Link } from "react-router-dom";

const services = [
  {
    id: "private-dining",
    tag: "01",
    title: "Private Dining",
    headline: "Intimate dining experiences, designed with precision, atmosphere, and quiet elegance.",
    description: "Each table is curated to reflect your setting, your guests, and your vision — down to the finest detail.",
    tags: ["Private Chef", "Sommelier Service", "Bespoke Menu", "Intimate Atmosphere"],
    image: "https://public.readdy.ai/ai/img_res/182ec0a602ecf06184f828e1d1fcdc36.jpg"
  },
  {
    id: "luxury-events",
    tag: "02",
    title: "Luxury Events",
    headline: "Elevated hospitality for private celebrations and exclusive gatherings.",
    description: "From concept to execution, every moment is orchestrated with refinement and intention.",
    tags: ["Event Design", "Full Atmosphere Curation", "Bespoke Florals", "Private Chef Team"],
    image: "https://readdy.ai/api/search-image?query=luxury%20event%20styled%20long%20dining%20table%20candelabras%20white%20florals%20crystal%20glasses%20gold%20accents%20soft%20candlelight%20elegant%20atmosphere%20evening%20refined%20wedding%20dinner&width=900&height=700&seq=tp-svc-2&orientation=landscape"
  },
  {
    id: "bespoke-hospitality",
    tag: "03",
    title: "Bespoke Hospitality",
    headline: "A fully tailored service experience, adapted to your environment and expectations.",
    description: "Discreet, polished, and seamlessly integrated into every moment.",
    tags: ["Private Chef", "Dedicated Staff", "Sommelier", "Complete Service"],
    image: "https://readdy.ai/api/search-image?query=luxury%20private%20chef%20hands%20plating%20exquisite%20dish%20fine%20dining%20white%20gloves%20elegant%20kitchen%20gold%20accents%20refined%20food%20art%20precision%20editorial&width=900&height=700&seq=tp-svc-3&orientation=landscape"
  },
  {
    id: "table-styling",
    tag: "04",
    title: "Table Styling & Atmosphere",
    headline: "The table is more than a setting — it is the essence of the experience.",
    description: "We compose every visual and sensory detail to create an atmosphere that feels both intimate and exceptional.",
    tags: ["Tablescape Design", "Bespoke Florals", "Lighting Curation", "Linen & Material Selection"],
    image: "https://readdy.ai/api/search-image?query=luxury%20tablescape%20artistic%20styling%20dried%20florals%20matte%20black%20plates%20gold%20cutlery%20linen%20textures%20crystal%20editorial%20still%20life%20elegant%20warm%20tones%20minimal%20beauty&width=900&height=700&seq=tp-svc-4&orientation=landscape"
  },
  {
    id: "corporate-hosting",
    tag: "05",
    title: "Corporate & Brand Hosting",
    headline: "Refined hospitality experiences for brands and corporate clients seeking distinction.",
    description: "Where presentation, service, and atmosphere align with your identity.",
    tags: ["Chairman's Dinners", "Client Entertainment", "Brand Activations", "Corporate Gifting Experiences"],
    image: "https://readdy.ai/api/search-image?query=elegant%20corporate%20private%20dinner%20luxury%20setting%20refined%20table%20gold%20accents%20dark%20moody%20candlelit%20selective%20guests%20exclusive%20gathering%20brand%20hospitality%20editorial&width=900&height=700&seq=tp-svc-5&orientation=landscape"
  }
];

export default function ServicesList() {
  const [activeId, setActiveId] = useState<string | null>(null);

  return (
    <section className="bg-[#FAF8F5] py-24 px-8 md:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="space-y-0">
          {services.map((svc, i) => (
            <div key={svc.id} className="border-t border-[#E0D8CE] last:border-b">
              {/* Accordion header */}
              <button
                className="w-full text-left py-8 flex items-start justify-between gap-8 cursor-pointer group"
                onClick={() => setActiveId(activeId === svc.id ? null : svc.id)}
                aria-expanded={activeId === svc.id}
              >
                <div className="flex items-start gap-8 flex-1 min-w-0">
                  <span className="font-serif text-[#C9A961]/50 text-sm mt-1 flex-shrink-0">{svc.tag}</span>
                  <div>
                    <h2 className="font-serif text-[#1A1A1A] text-2xl md:text-3xl group-hover:text-[#8A7F74] transition-colors duration-300">
                      {svc.title}
                    </h2>
                    <p className="text-[#8A7F74] text-sm font-light mt-1 italic">{svc.headline}</p>
                  </div>
                </div>
                <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 mt-1 transition-transform duration-400 ${activeId === svc.id ? "rotate-45" : ""}`}>
                  <i className={`ri-add-line text-xl text-[#C9A961]`} />
                </div>
              </button>

              {/* Expanded content */}
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  activeId === svc.id ? "max-h-[600px] opacity-100 pb-12" : "max-h-0 opacity-0"
                }`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 pl-0 md:pl-16">
                  <div>
                    <p className="text-[#8A7F74] text-base font-light leading-relaxed mb-8">
                      {svc.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-8">
                      {svc.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-4 py-1.5 border border-[#D8D0C4] text-[#8A7F74] text-xs tracking-[0.1em] uppercase font-light"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <Link
                      to="/experiences"
                      className="inline-flex items-center gap-2 text-sm text-[#1A1A1A] border-b border-[#1A1A1A] pb-0.5 tracking-[0.1em] uppercase font-light hover:text-[#C9A961] hover:border-[#C9A961] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Enquire About This Experience
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                  <div className="overflow-hidden rounded-lg" style={{ aspectRatio: "16/10" }}>
                    <img
                      src={svc.image}
                      alt={svc.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
